/** HttpOnly cookie session helpers — do not store tokens in localStorage. */

export const SESSION_FETCH_OPTIONS = { credentials: "include" };

const SESSION_TIMEOUT_MS = 12_000;

/** Remove legacy bearer tokens from older clients. */
export function clearLegacySessionToken() {
  try {
    localStorage.removeItem("session_token");
  } catch {
    // ignore
  }
}

async function fetchWithTimeout(url, options = {}, timeoutMs = SESSION_TIMEOUT_MS) {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } finally {
    window.clearTimeout(timer);
  }
}

async function withTimeout(promise, timeoutMs, label = "operation") {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = window.setTimeout(() => reject(new Error(`${label} timed out`)), timeoutMs);
  });
  try {
    return await Promise.race([promise, timeout]);
  } finally {
    window.clearTimeout(timer);
  }
}

function isJsonSessionResponse(res) {
  if (!res.ok) return false;
  const contentType = res.headers.get("content-type") || "";
  return contentType.includes("application/json");
}

export async function verifySession() {
  try {
    const res = await fetchWithTimeout("/api/auth/me", SESSION_FETCH_OPTIONS);
    return isJsonSessionResponse(res);
  } catch {
    return false;
  }
}

/** If Supabase is signed in but the HttpOnly app cookie is missing, exchange again. */
export async function ensureBackendSession() {
  if (await verifySession()) return true;

  try {
    const { supabase } = await withTimeout(import("@/lib/supabase"), SESSION_TIMEOUT_MS, "supabase import");
    const { data } = await withTimeout(
      supabase.auth.getSession(),
      SESSION_TIMEOUT_MS,
      "supabase getSession"
    );
    const accessToken = data?.session?.access_token;
    if (!accessToken) return false;

    const res = await fetchWithTimeout("/api/auth/session", {
      method: "POST",
      ...SESSION_FETCH_OPTIONS,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        supabase_access_token: accessToken,
        invite_token: sessionStorage.getItem("pending_invite_token") || undefined,
      }),
    });

    if (!isJsonSessionResponse(res)) return false;
    clearLegacySessionToken();
    return verifySession();
  } catch {
    return false;
  }
}
